import React from 'react';
import { useWizard, WizardStep } from '@zillow/zrs-rental-applications';
import { DetailedIconProfile, Flex, Heading, List, Paragraph } from '@zillow/constellation';
import { useIdVerificationCallbacks, useSkipTermsStep, useTermSet } from '../../contexts/IdVerificationContext';
import { IdvStep, TermSet } from '../../../..';
import { LANDLORD_CLICKSTREAM_EVENTS } from '../../clickstream/events';

interface LandingStepProps {
  id: string;
  label: string;
}

function LandlordContent() {
  return (
    <Flex display="flex" flexDirection="column" gap="sm">
      <DetailedIconProfile size="md" />
      <Heading level={4} as="h2">
        Verify your identity to view applications
      </Heading>
      <Paragraph>
        To protect applicants’ confidential information, the Fair Credit Reporting Act requires that we make sure it’s
        really you.
      </Paragraph>
      <Paragraph>You’ll be asked:</Paragraph>
      <List>
        <List.Item>Your full legal name and current address</List.Item>
        <List.Item>Your Social Security number (optional)</List.Item>
        <List.Item>Consent for Experian’s and CIC’s terms and conditions</List.Item>
        <List.Item>Additional security questions that Experian uses to confirm your identity</List.Item>
      </List>
      <Paragraph>Once completed, your verification will be valid for 335 days.</Paragraph>
    </Flex>
  );
}

function RenterContent() {
  return (
    <Flex display="flex" flexDirection="column" gap="sm">
      <DetailedIconProfile size="md" />
      <Heading level={4} as="h2">
        Verify your identity
      </Heading>
      <Paragraph>
        The information collection in this step is used to verify your identity. This helps ensure that your credit
        history and background report are accurate, secure, and protected. These reports are only shared with rental
        managers who have also verified their identities.
      </Paragraph>
      <Paragraph>You’ll be asked to provide:</Paragraph>
      <List>
        <List.Item>Your full legal name, current address, and mobile phone number</List.Item>
        <List.Item>Your date of birth</List.Item>
        <List.Item>Your Social Security number (optional)</List.Item>
        <List.Item>
          Consent to terms from Experian and Contemporary Information Corp. (CIC) to perform credit and background
          checks
        </List.Item>
      </List>
      <Paragraph>Experian may also ask additional security questions to help confirm your identity.</Paragraph>
    </Flex>
  );
}

export default function LandingStep({ id, label }: LandingStepProps) {
  const { onRequestStep } = useWizard();
  const termSet = useTermSet();
  const skipTermsStep = useSkipTermsStep();
  const { trackEvent } = useIdVerificationCallbacks();

  return (
    <WizardStep
      id={id}
      label={label}
      meta={{
        renderNextButton: 'Continue',
        renderBackButton: () => null,
      }}
      onRequestNext={(abortController: AbortController) => {
        trackEvent(LANDLORD_CLICKSTREAM_EVENTS.startIdVerify);
        if (skipTermsStep) {
          abortController.abort();
          onRequestStep(IdvStep.PersonalInfoStep);
        }
      }}
      render={() => {
        return termSet === TermSet.LandlordScreening ? <LandlordContent /> : <RenterContent />;
      }}
    />
  );
}
